import React, { useState, useEffect, useRef } from "react";
import { calculateAge } from "../utils/gameUtils";

const MAX_GUESSES = 6;
const CELL_COUNT = 6;
const STAGGER_MS = 150;
const FLIP_MIDPOINT_MS = 300;

const HEADERS = [
  { key: "club", label: "Club", tooltip: "What club does the player play for?" },
  { key: "league", label: "League", tooltip: "What league does the player play in?" },
  { key: "country", label: "Country", tooltip: "What country is the player from?" },
  {
    key: "position",
    label: "Position",
    tooltip:
      "In what position does the player play (Goalkeeper, Defender, Midfielder, Attacker)?",
  },
  {
    key: "number",
    label: "#",
    tooltip:
      "What is the player's number? YELLOW indicates a match within 5 numbers. (ex: #9 & #14)",
  },
  {
    key: "age",
    label: "Age",
    tooltip:
      "What is the player's age? YELLOW indicates a match within 2 years. (ex: 19 y/o & 21 y/o)",
  },
];

function GuessGrid({ rows }) {
  const [revealedCells, setRevealedCells] = useState(() => {
    const initial = new Set();
    for (let r = 0; r < rows.length; r++) {
      for (let c = 0; c < CELL_COUNT; c++) {
        initial.add(`${r}-${c}`);
      }
    }
    return initial;
  });
  const prevRowCountRef = useRef(rows.length);

  useEffect(() => {
    if (rows.length > prevRowCountRef.current) {
      const newRowIdx = rows.length - 1;
      const timers = [];

      for (let i = 0; i < CELL_COUNT; i++) {
        const timer = setTimeout(() => {
          setRevealedCells((prev) => {
            const next = new Set(prev);
            next.add(`${newRowIdx}-${i}`);
            return next;
          });
        }, i * STAGGER_MS + FLIP_MIDPOINT_MS);
        timers.push(timer);
      }

      prevRowCountRef.current = rows.length;
      return () => timers.forEach(clearTimeout);
    }
  }, [rows.length]);

  return (
    <div className="guess-grid">
      {HEADERS.map(({ label, tooltip }) => (
        <div key={label} className="grid-header">
          {label} <span className="tooltip">{tooltip}</span>
        </div>
      ))}

      {Array.from({ length: MAX_GUESSES }, (_, rowIdx) => {
        const row = rows[rowIdx];

        return (
          <React.Fragment key={rowIdx}>
            {row && (
              <div className="player-label">{row.guessData.displayName}</div>
            )}
            <div className="grid-row">
              {Array.from({ length: CELL_COUNT }, (_, cellIdx) => {
                if (!row) {
                  return <div key={cellIdx} className="grid-cell" />;
                }

                const cell = row.cells[cellIdx];
                const isRevealed = revealedCells.has(`${rowIdx}-${cellIdx}`);

                const hintClass = isRevealed
                  ? cell.hint === "correct"
                    ? "cell-correct"
                    : cell.hint === "partial"
                      ? "cell-partial"
                      : "cell-incorrect"
                  : "";

                return (
                  <div
                    key={cellIdx}
                    className={`grid-cell cell-flip ${hintClass}`}
                    style={{ animationDelay: `${cellIdx * STAGGER_MS}ms` }}
                  >
                    {isRevealed ? cell.value : ""}
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export function buildGuessRows(guessEntries, comparePlayers) {
  return guessEntries.map(({ guessData, hints }, rowIndex) => {
    const age = calculateAge(guessData.dateofbirth);
    const cells = [
      { value: guessData.club, hint: hints.club },
      { value: guessData.league, hint: hints.league },
      { value: guessData.country, hint: hints.country },
      { value: guessData.position, hint: hints.position },
      { value: guessData.number, hint: hints.number },
      { value: age, hint: hints.age },
    ];
    return { rowIndex, guessData, cells };
  });
}

export default GuessGrid;
